// One place that talks to the server.
//
// Everything is same-origin (Vite proxies /api in development, axum serves
// this build beside the API in production), so the session cookie travels
// automatically and there is no token to keep anywhere in JavaScript --
// which is the point of an HttpOnly cookie.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    // The browser attaches Origin on its own for non-GET, which is what the
    // server's CSRF check reads.
    credentials: "same-origin",
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? `request failed (${res.status})`);
  }
  return body as T;
}

export const get = <T>(p: string) => request<T>(p);
export const post = <T>(p: string, body?: unknown) =>
  request<T>(p, { method: "POST", body: JSON.stringify(body ?? {}) });
export const put = <T>(p: string, body: unknown) =>
  request<T>(p, { method: "PUT", body: JSON.stringify(body) });

// --- Shapes the server returns ------------------------------------------

export type Role = "admin" | "csm";

export interface Me {
  user: { id: string; email: string; name: string; role: Role; is_admin: boolean };
  customers: CustomerRef[];
}

export interface CustomerRef {
  id: string;
  name: string;
  display_name: string;
}

export interface ReportSummary {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_display_name: string;
  report_date: string;
  created_at: string;
  mode: string;
  window_days: number;
  uncovered_days: number;
  sentiment: string | null;
  executive_summary: string | null;
  highlights: number;
  lowlights: number;
  open_actions: number;
}

/** Persisted strings -- see `sage_core::classification`. Adding one means a
    migration widening the CHECK, a Rust variant, and an entry below. */
export type Category = "bfsi" | "non_bfsi";
export type Stage =
  | "pre_poc"
  | "demo_done"
  | "poc"
  | "msa"
  | "go_live"
  | "non_icp"
  | "lost";

export const CATEGORY_LABEL: Record<Category, string> = {
  bfsi: "BFSI",
  non_bfsi: "Non-BFSI",
};
export const CATEGORIES = Object.keys(CATEGORY_LABEL) as Category[];

/** In pipeline order: the way forward, then the two ways out. */
export const STAGE_LABEL: Record<Stage, string> = {
  pre_poc: "Pre-POC",
  demo_done: "Demo Done",
  poc: "POC",
  msa: "MSA",
  go_live: "Go-Live",
  non_icp: "Non-ICP",
  lost: "Lost",
};
export const STAGES = Object.keys(STAGE_LABEL) as Stage[];

/** Null is "nobody has set it yet", shown as Unset and filterable. */
export interface Classified {
  category: Category | null;
  stage: Stage | null;
}

export interface CustomerRow extends CustomerRef, Classified {
  domains: string[];
  latest_report: ReportSummary | null;
}

// --- A report, as the page lays it out -----------------------------------
//
// These mirror `sage_report::report` exactly, because that is what the API
// serves: `reports.report` is the jsonb the agent produced, and the page
// renders it directly rather than asking the server for HTML. Every string
// below originates in Slack or email we collected from outside, so it may
// only ever reach the DOM as a text node -- see the note on `Sources`.

export type ReportSentiment = "positive" | "neutral" | "mixed" | "negative";
export type ReportPriority = "high" | "medium" | "low";
/** What the *agent* says about an item, which is not the tracker's status. */
export type AgentStatus = "open" | "resolved" | "dropped";

/** Where a finding came from. `thread_id` matches a `SourceThread.id` when
    the agent cited something real; a ref it invented matches nothing, which
    is how an unsupported claim shows up as unsupported. */
export interface EvidenceRef {
  source: string;
  thread_id: string;
  quote: string | null;
}

export interface Finding {
  /** Writer-assigned and stable across reports; absent on reports written
      before findings had ids. */
  id?: string | null;
  /** Set when this restates a finding from the previous report. */
  carried_from?: string | null;
  first_seen?: string | null;
  /** One to three words: "Connect rate", "Go-live". */
  area?: string | null;
  title: string;
  detail: string;
  evidence: EvidenceRef[];
}

/** A highlight or lowlight first raised in the window -- `/api/findings`. */
export interface NewFinding {
  report_id: string;
  customer_id: string;
  customer: string;
  customer_display_name: string;
  report_date: string;
  /** When the run that raised it was written: "added". */
  created_at: string;
  kind: "highlight" | "lowlight";
  finding_id: string;
  area: string | null;
  title: string | null;
  detail: string | null;
  evidence: EvidenceRef[] | null;
}

export interface FindingsResponse {
  since: string;
  days: number;
  findings: NewFinding[];
  /** Every active customer in scope, including the ones with nothing new. */
  customers: CustomerRef[];
}

export interface ReportAction {
  /** Stable across reports, assigned by the writer. Absent on reports
      written before ids existed, which cannot be tracked by identity --
      these three are omitted entirely rather than sent as null. */
  id?: string | null;
  carried_from?: string | null;
  status: AgentStatus;
  title: string;
  detail: string;
  priority: ReportPriority;
  owner: string | null;
  due: string | null;
  first_seen?: string | null;
  evidence: EvidenceRef[];
}

/** A number a metrics finding rests on. */
export interface MetricRef {
  campaign: string;
  metric: string;
  on?: string | null;
  value: number;
  baseline?: number | null;
}

export interface MetricFinding {
  title: string;
  detail: string;
  severity: "info" | "warning" | "serious" | "critical";
  evidence: MetricRef[];
  signal?: string | null;
  /** Ours to fix rather than the customer's to explain. */
  platform_fault: boolean;
}

/**
 * The metrics half of a report.
 *
 * Absent for a customer who runs on no platform tenant — which is a
 * different statement from a customer whose campaigns did nothing, and the
 * report shows the difference by omitting the section rather than printing
 * an empty one.
 */
export interface MetricsBrief {
  summary: string;
  trajectory: "improving" | "steady" | "degrading" | "mixed";
  findings: MetricFinding[];
  open_questions: string[];
}

export interface ReportBody {
  executive_summary: string;
  sentiment: ReportSentiment;
  highlights: Finding[];
  lowlights: Finding[];
  action_items: ReportAction[];
  open_questions: string[];
  /** Present only when the customer runs on a platform tenant. Written by
   *  the orchestration from a deterministic detector, never by the report
   *  agent — the field is absent from the schema it is given. */
  platform?: MetricsBrief | null;
}

/** One stored run: the report itself plus what it covered and cost. */
export interface ReportDetail {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_display_name: string;
  /** A calendar date. Never put through a timezone -- see format.ts. */
  report_date: string;
  period_start: string;
  period_end: string;
  window_days: number;
  mode: string;
  uncovered_days: number;
  created_at: string;
  read_thread_ids: string[] | null;
  report: ReportBody;

  model: string | null;
  slack_messages: number | null;
  email_messages: number | null;
  cost_usd: number | null;
  llm_calls: number | null;
  tool_calls: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cached_input_tokens: number | null;
  reasoning_tokens: number | null;
  duration_ms: number | null;
  credits_delta_usd: number | null;
}

export interface SourceMessage {
  author: string;
  at: string;
  subject: string | null;
  /** Full and untruncated. The agent saw a shortened copy; a human checking
      a finding should not have to. */
  body: string;
}

export interface SourceThread {
  id: string;
  source: "slack" | "email";
  title: string;
  participants: string[];
  first_at: string;
  last_at: string;
  /** False for threads the agent listed in its survey and chose to skip --
      worth seeing, because what was passed over is part of the story. */
  read_by_agent: boolean;
  messages: SourceMessage[];
}

/** Served by `/reports/{id}/sources`, separately from the report: it re-reads
    every message in the window, so the page asks for it only on demand. */
export interface SourceList {
  threads: SourceThread[];
}

/**
 * What the *agent* suggested, derived from a report's jsonb. Owned by
 * nobody and rewritten every run -- it is an opinion, not a commitment.
 * `TrackedItem` below is what a suggestion becomes once a person decides
 * it is real.
 */
export interface SuggestedItem {
  report_id: string;
  customer_id: string;
  customer: string;
  report_date: string;
  action_id: string | null;
  status: string | null;
  priority: string | null;
  title: string | null;
  detail: string | null;
  owner: string | null;
  due: string | null;
  /** Set once somebody promoted it, so the list can stop inviting a
      duplicate and point at the item instead. */
  tracked_id: string | null;
  tracked_status: ActionStatus | null;
}

export type ActionStatus = "open" | "in_progress" | "blocked" | "resolved" | "cancelled";
export type Severity = "critical" | "high" | "medium" | "low";

/** A suggestion as the report page shows it, with whether it is tracked. */
export interface Suggestion {
  action_id: string | null;
  title: string;
  detail: string;
  priority: string;
  agent_status: string;
  owner: string | null;
  due: string | null;
  first_seen: string | null;
  tracked_id: string | null;
  tracked_status: ActionStatus | null;
}

export interface SuggestionList {
  report_id: string;
  report_date: string;
  customer: string;
  customer_display_name: string;
  suggestions: Suggestion[];
}

/** An action item somebody owns. */
export interface TrackedItem {
  id: string;
  customer_id: string;
  customer: string;
  customer_display_name: string;
  source_report_id: string | null;
  source_action_id: string | null;
  title: string;
  description: string | null;
  status: ActionStatus;
  severity: Severity;
  assignee_user_id: string | null;
  assignee_name: string | null;
  assignee_email: string | null;
  eta: string | null;
  resolution: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
  created_by_name: string | null;
  updated_at: string;
  is_open: boolean;
  comment_count: number;
}

/** One line of an item's history: something said, or something changed. */
export interface ActionEntry {
  id: string;
  kind: "comment" | "event";
  author_user_id: string | null;
  author_name: string | null;
  author_email: string | null;
  body: string;
  detail: unknown;
  created_at: string;
  edited_at: string | null;
}

export interface Assignable {
  id: string;
  name: string;
  email: string;
  role: Role;
}

export interface ActionDetailResponse {
  item: TrackedItem;
  activity: ActionEntry[];
  assignable: Assignable[];
  /** Closing is the assignee's call or an admin's; the server decides. */
  can_close: boolean;
}

export const STATUS_LABEL: Record<ActionStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  resolved: "Resolved",
  cancelled: "Cancelled",
};

/** Reuses the report palette: severity and priority mean the same thing to
    a reader, so they must not be two colour schemes. */
export const SEVERITY_CLASS: Record<Severity, string> = {
  critical: "high",
  high: "high",
  medium: "medium",
  low: "low",
};

/** What `/api/customers/{ref}` serves: enough to draw the page's header
    and label its tabs before either tab has loaded anything. */
export interface CustomerPage extends CustomerRef, Classified {
  domains: string[];
  /** Null for a CSM: what a customer's reports have cost is admin-only,
      and the server does not send it rather than trusting the page to
      leave it out. */
  report_runs: number | null;
  spend_usd: number | null;
  open_actions: number;
  /** Active users holding a grant for this customer. Admins are absent:
      they reach every customer without one. */
  assigned: AssignedUser[];
}

export interface AssignedUser {
  id: string;
  name: string;
  role: Role;
}

export interface CustomerDetail extends CustomerRef, Classified {
  domains: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  slack_channels: number;
  assigned_users: number;
  /** Voice-platform tenants, not the email `domains` above. */
  platform_domains: number;
}

/** Which analytics deployment a platform domain lives in. Part of its
    identity, never a label: a `domain_id` is unique inside one deployment
    and means nothing in the other. */
export type Region = "global" | "india";

/** One tenant of the voice platform, and whoever claims it.

    `name` and `identifier` are null when the mirror has never seen this
    domain -- an assignment made before a region first synced, or one that
    outlived a tenant deleted upstream. That is shown, not hidden: the
    association deliberately carries no foreign key into the mirror. */
export interface PlatformDomainRow {
  region: Region;
  domain_id: string;
  name: string | null;
  identifier: string | null;
  in_directory: boolean;
  is_present: boolean;
  first_seen_at: string | null;
  last_seen_at: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_display_name: string | null;
  /** Our own tenant. A warning, never a refusal. */
  is_internal?: boolean;
}

/** How one region's last refresh went. Per region because "india is fine,
    global is recovering" is the normal state. */
export interface PlatformDomainSync {
  region: Region;
  last_run_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  domains_seen: number | null;
  domains_added: number | null;
  domains_absent: number | null;
}

export interface PlatformDomainDirectory {
  domains: PlatformDomainRow[];
  syncs: PlatformDomainSync[];
  /** Regions this deployment has a Redshift target for. Empty means none,
      and the screen says so rather than offering a Refresh that cannot work. */
  regions: Region[];
}

export interface RefreshResult {
  region: Region;
  outcome: { seen: number; added: number; absent: number } | null;
  error: string | null;
}

export interface UserSummary {
  id: string;
  email: string;
  name: string;
  role: Role;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  customers: string | null;
}

export interface ChannelDetail {
  id: string;
  slack_channel_id: string;
  customer_id: string | null;
  customer_name: string | null;
  name: string | null;
  is_private: boolean;
  is_enabled: boolean;
  backfill_days: number;
  last_polled_at: string | null;
}

export interface MailboxDetail {
  id: string;
  address: string;
  user_id: string | null;
  is_enabled: boolean;
  backfill_days: number;
  last_polled_at: string | null;
}

export interface AuditRow {
  id: string;
  at: string;
  actor_email: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  customer_id: string | null;
  detail: unknown;
}

// --- Platform metrics ---------------------------------------------------

/**
 * Counts plus the rates derived from them.
 *
 * Every rate is nullable, and that is information rather than laziness: a
 * campaign that placed no calls has no connect rate, which is not the same
 * as a connect rate of zero. The server sends null; the UI renders an em
 * dash.
 */
export interface Summary {
  leads_created: number;
  journeys_created: number;
  calls_total: number;
  attempts: number;
  connected: number;
  voicemail: number;
  ring_no_answer: number;
  trigger_failed: number;
  scheduled: number;
  cancelled: number;
  talk_seconds: number;
  journeys_touched: number;
  groups_touched: number;
  connect_rate: number | null;
  attempts_per_lead: number | null;
  avg_talk_seconds: number | null;
  journeys_per_lead: number | null;
}

export interface DayPoint extends Summary {
  /** A calendar date, already bucketed in the campaign's own timezone by
   *  the server. Never converted here — converting a calendar date shifts
   *  it by a day for anybody west of UTC. */
  metric_date: string;
}

export interface CampaignPoint extends Summary {
  campaign_id: string;
  /** Null when the directory mirror has not seen this campaign yet. */
  identifier: string | null;
  name: string | null;
  region: string;
  bucket_tz: string;
}

/**
 * One detected movement, with the arithmetic that produced it.
 *
 * Computed in Rust, not by a model: an agent asked "did volume drop?" is
 * sometimes wrong and always fluent, so the numbers are decided
 * deterministically and only the prose around them is generated.
 */
export interface Signal {
  kind:
    | "no_calls"
    | "no_leads"
    | "call_volume_down"
    | "lead_volume_down"
    | "lead_volume_up"
    | "connect_rate_down"
    | "attempts_per_lead_down"
    | "trigger_failure_burst";
  severity: "info" | "warning" | "serious" | "critical";
  campaign_id: string;
  campaign: string | null;
  on: string;
  days_running: number;
  metric: string;
  observed: number;
  baseline: number;
  deviation: number;
  baseline_samples: number;
  robust_z: number | null;
  /** A plain sentence, produced server-side so both surfaces read alike. */
  describe: string;
}

export interface MetricsPage {
  from: string;
  to: string;
  campaign: string | null;
  totals: Summary;
  days: DayPoint[];
  campaigns: CampaignPoint[];
  /** Anomalies as of now — deliberately not affected by the window picker,
   *  which controls the charts. */
  signals: Signal[];
  /** How many complete days the detector evaluated. */
  signal_days: number;
  /** Whether any tenant is mapped at all — a different thing from having
   *  no traffic, and the page says so rather than showing empty charts. */
  has_platform_domains: boolean;
}

/** A campaign the metrics filter can offer, with the domain it runs under. */
export interface MetricCampaign {
  region: string;
  domain_id: string;
  domain_identifier: string | null;
  campaign_id: string;
  campaign_identifier: string | null;
  campaign_name: string | null;
  /** The last day it placed or queued a call; null if it never has. */
  last_active: string | null;
  recent_attempts: number;
}

/** An inclusive range of calendar dates. */
export interface DateSpan {
  start: string;
  end: string;
}

/** One column of the weeks-and-months comparison. */
export interface PeriodPoint {
  kind: "week" | "month";
  /** 0 is the current period, 1 the one before, and so on. */
  offset: number;
  span: DateSpan;
  /** The current period: it ends today, not at its calendar end. */
  to_date: boolean;
  /** For a to-date period: the same elapsed days of the one before. */
  like_for_like: DateSpan | null;
  summary: Summary;
  like_for_like_summary: Summary | null;
  /** The rollup's history starts after this period does, so its totals
   *  are a floor rather than a figure. */
  partial_history: boolean;
}

export interface PeriodsPage {
  /** Today on the business calendar (IST). */
  today: string;
  campaign: string | null;
  history_from: string | null;
  weeks: PeriodPoint[];
  months: PeriodPoint[];
}

// --- Customer notes -----------------------------------------------------

/** Which note: agreed targets and definitions, or the running status. */
export type NoteKind = "kpi" | "updates";

/** One saved version. Rows are append-only: a restore is a new version
    with `restored_from` set, never an edit of an old one. */
export interface NoteVersion {
  version: number;
  body: string;
  restored_from: number | null;
  author_name: string | null;
  author_email: string;
  /** The author's role when they wrote it, not today's. */
  author_role: Role;
  created_at: string;
}

export interface NoteSummary {
  kind: NoteKind;
  /** Null until somebody writes the first version. */
  current: NoteVersion | null;
  version_count: number;
}

export interface Notes {
  can_edit: boolean;
  notes: NoteSummary[];
}

const notesPath = (customer: string) => `/api/customers/${customer}/notes`;

export const getNotes = (customer: string) => get<Notes>(notesPath(customer));

export const getNoteVersions = (customer: string, kind: NoteKind) =>
  get<{ versions: NoteVersion[] }>(`${notesPath(customer)}/${kind}/versions`).then(
    (r) => r.versions,
  );

/** `baseVersion` is the version the editor opened on (0 for a first
    write). If the note has moved on since, the server answers 409 and
    keeps nothing -- the draft stays with the caller. */
export const saveNote = (customer: string, kind: NoteKind, body: string, baseVersion: number) =>
  post<NoteVersion>(`${notesPath(customer)}/${kind}`, { body, base_version: baseVersion });

export const restoreNote = (
  customer: string,
  kind: NoteKind,
  version: number,
  baseVersion: number,
) =>
  post<NoteVersion>(`${notesPath(customer)}/${kind}/restore`, {
    version,
    base_version: baseVersion,
  });

// --- Revenue -------------------------------------------------------------

/** Money and rates travel as decimal strings, never floats (invariant 49). */
export type Money = string;

export type MonthStatus = "frozen" | "live" | "pending" | "not_computed";

export interface Projection {
  factor: string;
  tier_label: string;
  bot_billable_minutes: string;
  total_amount: Money;
}

export interface MonthFigures {
  month: string;
  status: MonthStatus;
  currency: string;
  estimated: boolean;
  plan_version: number | null;
  tier_label: string;
  below_mmc_minutes: string | null;
  calls: number;
  connected_billable_secs: number;
  voicemail_billable_secs: number;
  bot_billable_secs: number;
  dialer_billable_secs: number;
  /** Calls charged per call, and their part of bot/dialer amount. */
  bot_flat_calls: number;
  bot_flat_amount: Money;
  dialer_flat_calls: number;
  dialer_flat_amount: Money;
  bot_amount: Money;
  dialer_amount: Money;
  total_amount: Money;
  statement_id: string | null;
  computed_at: string | null;
  source: string | null;
  projection: Projection | null;
}

export type RevenueSubject =
  | { type: "customer"; customer_id: string }
  | { type: "tenant"; region: Region; domain_id: string };

export interface SubjectRevenue {
  subject: RevenueSubject;
  name: string;
  slug: string;
  detail: string;
  has_plan: boolean;
  /** The customer's stage today; null for a tenant or an unstaged customer. */
  stage: Stage | null;
  months: MonthFigures[];
}

export interface RevenueTotal {
  month: string;
  currency: string;
  subjects: number;
  calls: number;
  connected_billable_secs: number;
  voicemail_billable_secs: number;
  bot_billable_secs: number;
  dialer_billable_secs: number;
  bot_flat_calls: number;
  bot_flat_amount: Money;
  dialer_flat_calls: number;
  dialer_flat_amount: Money;
  bot_amount: Money;
  dialer_amount: Money;
  total_amount: Money;
  projected_amount: Money | null;
  below_mmc: number;
  estimated: number;
  frozen: number;
}

export interface RevenueSync {
  region: Region;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
}

export interface RevenueOverview {
  today: string;
  months: string[];
  customers: SubjectRevenue[];
  customer_totals: RevenueTotal[];
  /** Admin only; absent, not empty, for anybody else. */
  unmapped?: SubjectRevenue[];
  unmapped_totals?: RevenueTotal[];
  syncs: RevenueSync[];
}

/** How a rule's calls are paid for. Either way its pulse measures them
    toward usage. */
export type Charge = "per_minute" | "per_call";
export interface PulseRule {
  up_to_secs: number;
  pulse_secs: number;
  charge?: Charge;
}
export interface Pulse {
  rules: PulseRule[];
  default_pulse_secs: number;
  default_charge?: Charge;
}
/** The rate_per_call key of the line past the last rule. */
export const DEFAULT_RULE_KEY = "default";
export interface Tier {
  label: string;
  from_minutes: number;
  rate_per_min: string;
  /** One price per per-call rule, keyed by its up_to_secs or "default". */
  rate_per_call?: Record<string, string>;
}
export type TierBasis = "bot_minutes" | "dialer_minutes";
export interface DialerCharge {
  pulse: Pulse;
  tiers: Tier[];
  tier_basis: TierBasis;
}
export type PlanMatch =
  | { domain: { region: Region; domain_id: string } }
  | { campaign: { region: Region; campaign_id: string } };
export interface PlanOverride {
  match: PlanMatch;
  label: string;
  bot?: { pulse?: Pulse; tiers?: Tier[] };
  dialer?: { enabled: boolean; pulse?: Pulse; tiers?: Tier[]; tier_basis?: TierBasis };
  separate_pool: boolean;
}
export interface Plan {
  bot: { pulse: Pulse; tiers: Tier[] };
  dialer: DialerCharge | null;
  overrides: PlanOverride[];
}

export interface BillingPlanRow {
  id: string;
  customer_id: string | null;
  version: number;
  effective_from: string;
  currency: string;
  plan: Plan;
  note: string;
  created_by_name: string | null;
  created_at: string;
}

export interface CustomerRevenue {
  customer_id: string;
  months: MonthFigures[];
  plans: BillingPlanRow[];
  syncs: RevenueSync[];
}

export interface RevenueLine {
  region: Region;
  domain_id: string;
  campaign_id: string;
  kind: "connected" | "voicemail";
  pricing_group: string;
  domain_identifier: string | null;
  campaign_name: string | null;
  calls: number;
  raw_secs: number;
  bot_billable_secs: number;
  bot_per_minute_secs: number;
  bot_flat_calls: number;
  bot_flat_amount: string;
  dialer_billable_secs: number;
  dialer_per_minute_secs: number;
  dialer_flat_calls: number;
  dialer_flat_amount: string;
  bot_rate: string;
  dialer_rate: string | null;
  bot_amount: string;
  dialer_amount: string;
}

export interface RevenueGroup {
  key: string;
  label: string;
  pool: string;
  pool_usage_minutes: string;
  bot_tier: string;
  bot_rate: string;
  dialer_tier: string | null;
  dialer_rate: string | null;
  bot_call_rates?: Record<string, string>;
  dialer_call_rates?: Record<string, string> | null;
  bot_billable_secs: number;
  dialer_billable_secs: number;
  below_mmc_minutes: string | null;
}

export interface RevenueMonthDetail {
  figures: MonthFigures;
  groups: RevenueGroup[];
  plan: Plan | null;
  reason: string | null;
  frozen_by_name: string | null;
  lines: RevenueLine[];
  history: MonthFigures[];
}

export interface PreviewFigures {
  tier_label: string;
  below_mmc_minutes: string | null;
  bot_billable_secs: number;
  dialer_billable_secs: number;
  bot_flat_calls: number;
  bot_flat_amount: Money;
  dialer_flat_calls: number;
  dialer_flat_amount: Money;
  bot_amount: Money;
  dialer_amount: Money;
  total_amount: Money;
}

export interface PlanPreview {
  month: string;
  to_date: boolean;
  draft: PreviewFigures;
  current: PreviewFigures;
  current_estimated: boolean;
}

export interface MonthRun {
  month: string | null;
  stored: number;
  skipped: string[];
}

// --- Revenue briefs (invariant 51) ---------------------------------------
//
// Numbers in a brief are the digest's, copied in by the server; the prose
// is the model's. Every figure on the card comes from `value` / `prior`
// below, never from the text.

export type RevenueDirection = "up" | "flat" | "down" | "mixed";
export type RevenueUnit = "money" | "minutes" | "share" | "count" | "label";

export interface RevenueFact {
  id: string;
  label: string;
  unit: RevenueUnit;
  value?: Money;
  prior?: Money;
  text?: string;
  prior_text?: string;
}

export interface RevenueSignal {
  kind: string;
  severity: "info" | "notable" | "serious";
  facts: string[];
  delta_abs?: Money;
  delta_pct?: Money;
  months_running: number;
  describe: string;
}

export interface RevenueDigest {
  month: string;
  currency: string;
  state: "frozen" | "pending" | "live" | "not_computed";
  partial: boolean;
  prior_month: string | null;
  unfrozen_months: string[];
  direction: RevenueDirection;
  facts: RevenueFact[];
  signals: RevenueSignal[];
}

export interface RevenueRef {
  fact: string;
  label?: string;
  unit?: RevenueUnit;
  value?: Money;
  prior?: Money;
  text?: string;
  prior_text?: string;
  resolved: boolean;
}

export interface RevenueFinding {
  id?: string;
  title: string;
  detail: string;
  evidence: RevenueRef[];
  signal?: string;
}

export interface RevenueBrief {
  headline: string;
  direction: RevenueDirection;
  highlights: RevenueFinding[];
  lowlights: RevenueFinding[];
  open_questions: string[];
}

export interface CustomerRevenueBrief {
  digest: RevenueDigest;
  brief: RevenueBrief | null;
  model_called: boolean;
  created_at: string | null;
  awaiting: boolean;
}

export interface RevenueMover {
  slug: string;
  name: string;
  stage: string | null;
  direction: RevenueDirection;
  headline: string;
  total: Money | null;
  prior: Money | null;
  delta: Money | null;
}

export interface PortfolioBrief {
  month: string;
  groups: {
    currency: string;
    up: RevenueMover[];
    down: RevenueMover[];
    up_count: number;
    down_count: number;
    steady: number;
  }[];
  awaiting: { slug: string; name: string }[];
}

// --- Pace: the month so far against the same days of last month ----------

export type PaceState = "behind" | "on_pace" | "ahead" | "new" | "stopped";

export interface CampaignPace {
  campaign_id: string;
  name: string;
  mtd: Money;
  prior_mtd: Money;
  prior_month: Money;
  delta: Money;
  delta_pct: Money | null;
  last_billed: string | null;
  idle_days: number | null;
  state: PaceState;
}

export interface CustomerPace {
  customer_id: string;
  slug: string;
  name: string;
  stage: string | null;
  currency: string;
  as_of: string;
  days_compared: number;
  days_in_month: number;
  too_early: boolean;
  estimated: boolean;
  mtd: Money;
  prior_mtd: Money;
  prior_month: Money | null;
  delta: Money;
  delta_pct: Money | null;
  state: PaceState;
  projected_total: Money | null;
  mmc_at_risk: Money | null;
  tier_change: { from: string; to: string; down: boolean } | null;
  campaigns: CampaignPace[];
}

export interface PaceLine {
  slug: string;
  name: string;
  stage: string | null;
  state: PaceState;
  mtd: Money;
  prior_mtd: Money;
  delta: Money;
  delta_pct: Money | null;
  lead: CampaignPace | null;
}

export interface PortfolioPace {
  as_of: string | null;
  days_compared: number;
  days_in_month: number;
  too_early: boolean;
  groups: {
    currency: string;
    mtd: Money;
    prior_mtd: Money;
    behind: PaceLine[];
    ahead: PaceLine[];
    behind_count: number;
    ahead_count: number;
    on_pace: number;
    stopped: { slug: string; name: string; campaign: CampaignPace }[];
    at_risk: {
      slug: string;
      name: string;
      mmc_short_minutes: Money | null;
      tier_change: { from: string; to: string; down: boolean } | null;
      projected_total: Money | null;
    }[];
  }[];
}
